import React, { useState, useEffect } from 'react';
import { AddArtifactData, ProcessRole } from '../../types/processDefinition';
import { PrimeArtifactService } from '../../services/primeArtifactService';
import { PrimeArtifact } from '../../types/primeArtifact';
import './AddArtifactDialog.css';

interface AddArtifactDialogProps {
  processId: string;
  onSave: (artifactData: AddArtifactData) => void;
  onCancel: () => void;
}

export const AddArtifactDialog: React.FC<AddArtifactDialogProps> = ({ onSave, onCancel }) => {
  const [selectedArtifact, setSelectedArtifact] = useState<string>('');
  const [selectedRole, setSelectedRole] = useState<ProcessRole>('PROCESSITEM');
  const [inputStateId, setInputStateId] = useState<string>('');
  const [outputStateId, setOutputStateId] = useState<string>('');
  const [artifacts, setArtifacts] = useState<PrimeArtifact[]>([]);
  const [states, setStates] = useState<{ id: string; name: string }[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void loadArtifacts();
  }, []);

  useEffect(() => {
    if (selectedArtifact) {
      void loadArtifactStates(selectedArtifact);
    }
  }, [selectedArtifact]);

  const loadArtifacts = async () => {
    try {
      setLoading(true);
      const data = await PrimeArtifactService.getPrimeArtifacts();
      setArtifacts(data);
    } catch (error) {
      console.error('Failed to load artifacts:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadArtifactStates = async (artifactId: string) => {
    try {
      const data = await PrimeArtifactService.getPrimeArtifactStates(artifactId);
      setStates(data);
    } catch (error) {
      console.error('Failed to load artifact states:', error);
    }
  };

  const handleSave = () => {
    if (!selectedArtifact || !inputStateId || !outputStateId) {
      alert('Please select an artifact and both input and output states.');
      return;
    }

    const artifactData: AddArtifactData = {
      artifact_id: selectedArtifact,
      role: selectedRole,
      input_state_id: inputStateId,
      output_state_id: outputStateId,
    };

    onSave(artifactData);
  };

  const roleOptions: { value: ProcessRole; label: string; description: string }[] = [
    {
      value: 'PROCESSITEM',
      label: 'Process Item',
      description: 'The primary artifact being processed',
    },
    { value: 'MATERIAL', label: 'Material', description: 'Consumed or transformed in the process' },
    { value: 'RESOURCE', label: 'Resource', description: 'Required for process execution' },
    {
      value: 'PROCESSITEM_PERSISTENT',
      label: 'Persistent Process Item',
      description: 'Process item that persists through the process',
    },
    { value: 'CREATION', label: 'Creation', description: 'New artifact created by the process' },
  ];

  if (loading) {
    return (
      <div className="add-artifact-dialog-overlay">
        <div className="add-artifact-dialog">
          <div className="loading">Loading artifacts...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="add-artifact-dialog-overlay">
      <div className="add-artifact-dialog">
        <div className="dialog-header">
          <h2>Add Artifact to Process</h2>
          <button onClick={onCancel} className="close-btn">
            ×
          </button>
        </div>

        <div className="dialog-content">
          <div className="form-group">
            <label htmlFor="artifact-select">Artifact:</label>
            <select
              id="artifact-select"
              value={selectedArtifact}
              onChange={(e) => setSelectedArtifact(e.target.value)}
              className="form-select"
            >
              <option value="">Select an artifact...</option>
              {artifacts.map((artifact) => (
                <option key={artifact.id} value={artifact.id}>
                  {artifact.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="role-select">Role:</label>
            <select
              id="role-select"
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value as ProcessRole)}
              className="form-select"
            >
              {roleOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <div className="role-description">
              {roleOptions.find((opt) => opt.value === selectedRole)?.description}
            </div>
          </div>

          {selectedArtifact && (
            <>
              <div className="form-group">
                <label htmlFor="input-state-select">Input State:</label>
                <select
                  id="input-state-select"
                  value={inputStateId}
                  onChange={(e) => setInputStateId(e.target.value)}
                  className="form-select"
                >
                  <option value="">Select input state...</option>
                  {states.map((state) => (
                    <option key={state.id} value={state.id}>
                      {state.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="output-state-select">Output State:</label>
                <select
                  id="output-state-select"
                  value={outputStateId}
                  onChange={(e) => setOutputStateId(e.target.value)}
                  className="form-select"
                >
                  <option value="">Select output state...</option>
                  {states.map((state) => (
                    <option key={state.id} value={state.id}>
                      {state.name}
                    </option>
                  ))}
                </select>
              </div>
            </>
          )}
        </div>

        <div className="dialog-actions">
          <button onClick={onCancel} className="cancel-btn">
            Cancel
          </button>
          <button onClick={handleSave} className="save-btn">
            Add Artifact
          </button>
        </div>
      </div>
    </div>
  );
};
